import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { makeStyles } from "@mui/styles";
import TableContainer from "@mui/material/Paper";
import MuiTable from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import Pagination from "./Pagination";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { SimpleTextInput } from "components/FormTextInput";
import { Checkbox, Tooltip, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { navigateFromRow, opacity2hex } from "utils/miscUtils";

interface HeadProps {
  columns: any[];
  onColumnClick?: (column: any) => void;
  totalData?: any;
  highlightAltRow?: boolean;
  selectable?: boolean;
  allSelected?: boolean;
  someSelected?: boolean;
  onToggleAll?: () => void;
}

const useStyles = makeStyles((theme) => ({
  tableHead: {
    fontWeight: "600 !important",
    backgroundColor: "#1d1030",
    fontSize: 12,
    padding: 4,
    color: "#D3D3D3",
  },
  sortLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    cursor: "pointer",
  },
  headAlignRight: {
    justifyContent: "flex-end !important",
    textAlign: "center",
  },
  headAlignCenter: {
    justifyContent: "center !important",
    textAlign: "center",
  },
  icon: {
    marginLeft: 2,
    fontSize: 18,
  },
  tableSubHead: {
    fontWeight: "700 !important",
    fontSize: 12,
    padding: 8,
    backgroundColor: theme.palette.common.offWhite,
  },
  bodyCell: {
    padding: "8px 8px 8px 6px",
    maxHeight: "120px !important",
    overflowX: "auto",
  },
  bodyCellContent: {
    fontSize: 12,
    fontWeight: "500 !important",
  },
  bodyCellContent2: {
    fontSize: 11,
    fontWeight: "600 !important",
    color: "red",
  },
  padding: {
    padding: "8px 10px 8px 6px",
  },
  borderRight: {
    borderRight: 1,
    // borderWidth: 0,
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderRightColor: "#45454510",
  },
  grey: {
    // backgroundColor: theme.palette.common.offWhite,
    backgroundColor: "#d3d3d3" + opacity2hex("0.30"),
  },
  modalPreview: {
    height: 50,
    cursor: "pointer",
    alignContent: "center",
    color: theme.palette.primary.main,
  },
  green: {
    color: "green",
  },
  red: {
    color: "red",
  },
  lightBlue: {
    color: "rgb(70 30 192 / 87%)",
  },
  disabledRow: {
    backgroundColor: "#A9A9A9",
    opacity: 0.7,
    borderTop: "1px solid grey",
    borderBottom: "1px solid grey",
  },
  showMoreBtn: { color: "#007bff", cursor: "pointer" },
  rowLink: { color: "inherit", textDecoration: "none" },
  selectCell: { width: 44 },
  // The header checkbox sits on the dark header bar, so it needs light colours of its own.
  headCheckbox: {
    color: "#D3D3D3 !important",
    "&.Mui-checked": { color: "#ffffff !important" },
    "&.MuiCheckbox-indeterminate": { color: "#ffffff !important" },
  },
  // Painted on the cells, not the row, so the highlight also covers the alternating stripe and
  // the greyed background of a disabled record. Kept translucent so the row's own shade still
  // shows through instead of being replaced.
  clickableRow: {
    cursor: "pointer",
    "&:hover > td": {
      backgroundColor:
        theme.palette.secondary.light + opacity2hex("0.20") + " !important",
      transition: "background-color 120ms ease-in-out",
    },
  },
}));

const Head = ({
  columns,
  onColumnClick = () => {},
  totalData,
  highlightAltRow,
  selectable,
  allSelected,
  someSelected,
  onToggleAll,
}: HeadProps) => {
  const classes = useStyles();

  return (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell
            padding="checkbox"
            className={classNames(classes.tableHead, classes.selectCell)}
          >
            <Checkbox
              size="small"
              className={classes.headCheckbox}
              checked={!!allSelected}
              indeterminate={!allSelected && !!someSelected}
              onChange={() => onToggleAll?.()}
              inputProps={{ "aria-label": "Select all rows on this page" }}
            />
          </TableCell>
        )}
        {columns.map((column, _index) => (
          <Tooltip
            key={_index}
            title={column.tooltip || ""}
            placement="top-start"
            arrow
          >
            <TableCell
              key={column.id || `head-${_index}`}
              align={column?.alignHead || "left"}
              className={classNames(classes.tableHead, {
                [classes.borderRight]: highlightAltRow,
              })}
              onClick={() => onColumnClick(column)}
            >
              <Box
                className={classNames(classes.sortLabel, {
                  [classes.headAlignRight]: column?.alignHead === "right",
                  [classes.headAlignCenter]: column?.alignHead === "center",
                })}
              >
                {column.label}
                {column?.isSortable && (
                  <SwapVertIcon className={classes.icon} />
                )}
              </Box>
            </TableCell>
          </Tooltip>
        ))}
      </TableRow>

      {totalData?.data && (
        <TableRow>
          {selectable && <TableCell padding="checkbox" />}
          <TableCell
            className={classNames(
              {
                [classes.borderRight]: highlightAltRow,
              },
              classes.tableSubHead,
            )}
            align={"center"}
          >
            Total
          </TableCell>
          {Object.keys(totalData.data).map((_key, index) => (
            <TableCell
              className={classNames(
                { [classes.borderRight]: highlightAltRow },
                _key === "ggr" && classes.grey,
                totalData?.cellStyleByColumn &&
                  totalData?.cellStyleByColumn[_key]
                  ? totalData?.cellStyleByColumn[_key]
                  : classes.tableSubHead,
              )}
              align={
                columns?.find((info) => info?.id === _key)?.alignBody || "left"
              }
              key={index}
              style={{ fontWeight: 600 }}
            >
              {totalData?.data[_key] || "-"}
            </TableCell>
          ))}
        </TableRow>
      )}
    </TableHead>
  );
};

const getNestedValue = (obj, path, defaultValue = "-") => {
  if (!obj || !path) return defaultValue;
  return path
    .split(".")
    .reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : defaultValue),
      obj,
    );
};

const Row = ({
  actions: Actions,
  actionHandlers,
  columns,
  data,
  rowId,
  onRowClick,
  rowHref,
  cellWidthStyle,
  tableFieldValue,
  highlightAltRow,
  selectable,
  selected,
  selectDisabledReason,
  onToggleRow,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState({});

  const href = rowHref ? rowHref(data) : null;
  // The first text cell is rendered as a real link so the browser's own "Open in new tab" and
  // Cmd/Ctrl+click work; the click itself is handled here so a plain click stays in this tab.
  let linkCellRendered = false;

  const toggleExpand = (key: any) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const _onShowLessAndMore = (e, k) => {
    e.stopPropagation();
    toggleExpand(k);
  };

  const handleRowClick = (event: any) => {
    if (!href) {
      onRowClick(data);
      return;
    }

    navigateFromRow(event, navigate, href);
  };

  return (
    <TableRow
      hover
      onClick={handleRowClick}
      className={classNames(classes.borderRight, {
        [classes.grey]: highlightAltRow && rowId % 2 !== 0,
        [classes.clickableRow]: !!href,
      })}
    >
      {selectable && (
        <TableCell
          padding="checkbox"
          className={classNames(classes.selectCell, {
            [classes.disabledRow]: data?.rowDisabled || false,
          })}
          // Without this the checkbox click bubbles up and navigates the row.
          onClick={(event) => event.stopPropagation()}
        >
          <Tooltip title={selectDisabledReason || ""} placement="right" arrow>
            <span>
              <Checkbox
                size="small"
                checked={!!selected}
                disabled={!!selectDisabledReason}
                onChange={() => onToggleRow?.(data)}
              />
            </span>
          </Tooltip>
        </TableCell>
      )}
      {columns.map((column, _index) => {
        if (column.id === "actions") {
          return (
            <TableCell
              key={column.id || `action-${_index}`}
              align={column?.alignAction || "left"}
              padding="checkbox"
              className={classNames(classes.bodyCell, {
                [classes.padding]: highlightAltRow,
                [classes.disabledRow]: data?.rowDisabled || false,
              })}
            >
              <Actions
                rowId={rowId}
                data={data}
                actionHandlers={actionHandlers}
              />
            </TableCell>
          );
        } else if (column.id === "field") {
          return (
            <TableCell
              align={column?.alignBody || "center"}
              key={column.id || `body-${_index}`}
              className={classNames(
                { [classes.padding]: highlightAltRow },
                { [classes.borderRight]: highlightAltRow },
                { [classes.disabledRow]: data?.rowDisabled || false },
              )}
            >
              <SimpleTextInput
                id={column?.name}
                name={column?.name}
                value={
                  tableFieldValue?.[column?.name]?.[data[column?.mapId]] || ""
                }
                onChange={(e) =>
                  column?.handleChange(data, e.target.value, e.target.name)
                }
              />
            </TableCell>
          );
        } else if (column.id === "thumbnail") {
          return (
            <TableCell
              align={column?.alignBody || "center"}
              key={column.id || `body-${_index}`}
              className={classNames(
                data?.cellStyleByColumn && data?.cellStyleByColumn[column.id]
                  ? data?.cellStyleByColumn[column.id]
                  : classes.bodyCell,
                { [classes.borderRight]: highlightAltRow },
                { [classes.disabledRow]: data?.rowDisabled || false },
              )}
            >
              <Box
                className={classes.modalPreview}
                onClick={(e: any) => {
                  e.stopPropagation();
                  actionHandlers?.openModal(data);
                }}
              >
                Preview
              </Box>
            </TableCell>
          );
        } else {
          const asLink = !!href && !column.renderCell && !linkCellRendered;
          if (asLink) linkCellRendered = true;

          return (
            <TableCell
              align={column?.alignBody || "left"}
              key={column.id || `body-${_index}`}
              className={classNames(
                data?.cellStyleByColumn && data?.cellStyleByColumn[column.id]
                  ? data?.cellStyleByColumn[column.id]
                  : classes.bodyCell,
                { [classes.padding]: highlightAltRow },
                { [classes.borderRight]: highlightAltRow },
                { [classes.disabledRow]: data?.rowDisabled || false },
              )}
            >
              {column.renderCell ? (
                column.renderCell(data, column.id)
              ) : (
                <Box
                  component={asLink ? "a" : "div"}
                  {...(asLink ? { href: `#${href}` } : {})}
                  className={asLink ? classes.rowLink : undefined}
                >
                  <Box
                    className={classNames(
                      cellWidthStyle || classes.bodyCellContent,
                      {
                        [classes.red]: data?.isRemoved === "true",
                        [classes.lightBlue]: data?.isNewRec === "true",
                      },
                    )}
                  >
                    {/* {data[column.id] ?? "-"} */}
                    {(() => {
                      const value = getNestedValue(data, column.id);
                      const isLongText =
                        typeof value === "string" && value.length > 100;
                      const key = `${rowId}-${column.id}`;

                      if (column.showMore && isLongText) {
                        return (
                          <>
                            {expanded[key]
                              ? value
                              : `${value.slice(0, 100)}... `}
                            <span
                              className={classes.showMoreBtn}
                              onClick={(e) => _onShowLessAndMore(e, key)}
                            >
                              {expanded[key] ? " show less" : "show more"}
                            </span>
                          </>
                        );
                      } else {
                        return value;
                      }
                    })()}
                  </Box>
                  {data?.diff && (
                    <Box className={classes.bodyCellContent2}>
                      {data?.diff[column.id]
                        ? `(${data?.diff[column.id]})`
                        : ""}
                    </Box>
                  )}
                  {data?.ratingsDelta && (
                    <Box
                      className={classNames({
                        [classes.green]:
                          Number(data?.ratingsDelta[column.id]) > 0,
                        [classes.red]:
                          Number(data?.ratingsDelta[column.id]) < 0,
                      })}
                    >
                      {data?.ratingsDelta[column.id]
                        ? `(δ ${
                            data?.ratingsDelta[column.id] > 0
                              ? `+${data?.ratingsDelta[column.id]}`
                              : data?.ratingsDelta[column.id]
                          })`
                        : ""}
                    </Box>
                  )}
                </Box>
              )}
            </TableCell>
          );
        }
      })}
    </TableRow>
  );
};

Row.defaultProps = {
  onRowClick: () => {},
  onColumnClick: () => {},
  actions: () => <></>,
};

const Body = ({
  actions,
  actionHandlers,
  rows,
  columns,
  onRowClick,
  rowHref,
  cellWidthStyle,
  tableFieldValue,
  highlightAltRow,
  selectable,
  isRowSelected,
  rowSelectDisabledReason,
  onToggleRow,
}) => {
  return (
    <TableBody>
      {rows?.length > 0 ? (
        rows.map((row, index) => (
          <Row
            key={row.id || index}
            actions={actions}
            actionHandlers={actionHandlers}
            columns={columns}
            data={row}
            rowId={index}
            onRowClick={onRowClick}
            rowHref={rowHref}
            cellWidthStyle={cellWidthStyle}
            tableFieldValue={tableFieldValue}
            highlightAltRow={highlightAltRow}
            selectable={selectable}
            selected={selectable && isRowSelected(row)}
            selectDisabledReason={
              selectable ? rowSelectDisabledReason?.(row) : null
            }
            onToggleRow={onToggleRow}
          />
        ))
      ) : (
        <TableRow>
          <TableCell
            align="center"
            colSpan={columns.length + (selectable ? 1 : 0)}
          >
            <Typography align="center">No data to show</Typography>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
};

function Table(props: any) {
  const {
    columns,
    actions,
    actionHandlers = null,
    data,
    totalData = null,
    onRowClick,
    rowHref = null,
    onColumnClick,
    currentPage,
    pageSize,
    totalEntries,
    onChangePage,
    onRowsPerPageChange,
    cellWidthStyle = null,
    hidePagination = false,
    highlightAltRow = false,
    tableFieldValue = {},
    disableMaxHeight = false,
    // Row selection is opt-in: without `selectable` the table renders exactly as before.
    selectable = false,
    selectedIds = [],
    onSelectionChange = null,
    getRowId = (row: any) => row?._id,
    rowSelectDisabledReason = null,
  } = props;

  const selectedSet = React.useMemo(
    () => new Set(selectedIds ?? []),
    [selectedIds],
  );

  // "Select all" covers the rows currently on screen, and only the ones that may actually be acted on.
  const selectableRows = React.useMemo(
    () =>
      selectable
        ? (data ?? []).filter((row: any) => !rowSelectDisabledReason?.(row))
        : [],
    [selectable, data, rowSelectDisabledReason],
  );
  const selectedOnPage = selectableRows.filter((row: any) =>
    selectedSet.has(getRowId(row)),
  ).length;
  const allSelected =
    selectableRows.length > 0 && selectedOnPage === selectableRows.length;

  const toggleRow = (row: any) => {
    const id = getRowId(row);
    const next = selectedSet.has(id)
      ? selectedIds.filter((selectedId: any) => selectedId !== id)
      : [...selectedIds, id];
    onSelectionChange?.(next);
  };

  const toggleAll = () => {
    const pageIds = selectableRows.map(getRowId);
    if (allSelected) {
      const pageIdSet = new Set(pageIds);
      onSelectionChange?.(selectedIds.filter((id: any) => !pageIdSet.has(id)));
      return;
    }
    onSelectionChange?.([...new Set([...selectedIds, ...pageIds])]);
  };

  return (
    <>
      {!hidePagination && (
        <Pagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalEntries={totalEntries}
          onChangePage={onChangePage}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}
      <TableContainer
        style={{
          width: "100%",
          maxHeight: disableMaxHeight ? "none" : "74vh",
          overflow: disableMaxHeight ? "visible" : "auto",
        }}
        elevation={0}
      >
        <MuiTable stickyHeader>
          <Head
            columns={props.columns}
            onColumnClick={onColumnClick}
            totalData={totalData}
            highlightAltRow={highlightAltRow}
            selectable={selectable}
            allSelected={allSelected}
            someSelected={selectedOnPage > 0}
            onToggleAll={toggleAll}
          />
          <Body
            actions={actions}
            actionHandlers={actionHandlers}
            rows={data}
            columns={columns}
            onRowClick={onRowClick}
            rowHref={rowHref}
            cellWidthStyle={cellWidthStyle}
            tableFieldValue={tableFieldValue}
            highlightAltRow={highlightAltRow}
            selectable={selectable}
            isRowSelected={(row: any) => selectedSet.has(getRowId(row))}
            rowSelectDisabledReason={rowSelectDisabledReason}
            onToggleRow={toggleRow}
          />
        </MuiTable>
      </TableContainer>
    </>
  );
}

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      numeric: PropTypes.bool,
    }),
  ),
  actions: PropTypes.any,
  data: PropTypes.array,
  totalData: PropTypes.object,
  actionHandlers: PropTypes.any,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  totalEntries: PropTypes.number,
  onRowClick: PropTypes.func,
  rowHref: PropTypes.func,
  onChangePage: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onColumnClick: PropTypes.func,
  cellWidthStyle: PropTypes.any,
  hidePagination: PropTypes.bool,
  highlightAltRow: PropTypes.bool,
  tableFieldValue: PropTypes.object,
  disableMaxHeight: PropTypes.bool,
  selectable: PropTypes.bool,
  selectedIds: PropTypes.array,
  onSelectionChange: PropTypes.func,
  getRowId: PropTypes.func,
  rowSelectDisabledReason: PropTypes.func,
};

export default Table;
